import * as moment from "moment";

export const DATE_RANGE_DEFAULT_HISTOGRAM_INTERVAL = {
  LAST_7_DAYS: "DAY",
  LAST_30_DAYS: "DAY",
  LAST_60_DAYS: "DAY",
  LAST_90_DAYS: "WEEK",
  LAST_YEAR: "MONTH",
} as const;

export const DATE_RANGE_DEFAULT_HISTOGRAM_INTERVAL_FOR_MOBILE_DEVICES = {
  LAST_7_DAYS: "DAY",
  LAST_30_DAYS: "WEEK",
  LAST_60_DAYS: "WEEK",
  LAST_90_DAYS: "WEEK",
  LAST_YEAR: "MONTH",
} as const;

const DAY_RANGE_TO_VALUE_DICTIONARY = {
  TODAY: 0,
  LAST_7_DAYS: 7,
  LAST_30_DAYS: 30,
  LAST_60_DAYS: 60,
  LAST_90_DAYS: 90,
} as const;

type DAY_RANGE_TO_VALUE_DICTIONARY_TYPEMAP = keyof typeof DAY_RANGE_TO_VALUE_DICTIONARY;

const WEEK_RANGE_TO_VALUE_DICTIONARY = {
  LAST_30_DAYS: 4,
  LAST_60_DAYS: 8,
  LAST_90_DAYS: 12,
  LAST_YEAR: 52,
} as const;

type WEEK_RANGE_TO_VALUE_DICTIONARY_TYPEMAP = keyof typeof WEEK_RANGE_TO_VALUE_DICTIONARY;

const MONTH_RANGE_TO_VALUE_DICTIONARY = {
  LAST_30_DAYS: 1,
  LAST_60_DAYS: 2,
  LAST_90_DAYS: 3,
  LAST_YEAR: 12,
} as const;

type MONTH_RANGE_TO_VALUE_DICTIONARY_TYPEMAP = keyof typeof MONTH_RANGE_TO_VALUE_DICTIONARY;

export type RANGE_VALUE =
  | DAY_RANGE_TO_VALUE_DICTIONARY_TYPEMAP
  | WEEK_RANGE_TO_VALUE_DICTIONARY_TYPEMAP
  | MONTH_RANGE_TO_VALUE_DICTIONARY_TYPEMAP;

const sortDateStringArray = (date1: string, date2: string) => {
  const [year1, month1, day1] = date1.split("-");
  const [year2, month2, day2] = date2.split("-");

  if (year1 !== year2) {
    return +year1 - +year2;
  }

  if (month1 !== month2) {
    return +month1 - +month2;
  }

  return +day1 - +day2;
};

export const getMonthNameByDate = (dateString: moment.MomentInput) => {
  const monthName = moment(dateString, "YYYY-MM-DD").format("MMMM");
  const formatedMonthName = monthName.slice(0, 1).toUpperCase() + monthName.slice(1);

  return formatedMonthName;
};

export const getMonthPeriodDates = (dateString: moment.MomentInput) => {
  const dateFormat = "YYYY-MM-DD";
  const toDate = moment(dateString, dateFormat);
  const fromDate = toDate.clone().startOf("month").format(dateFormat);

  return { fromDate, toDate: dateString };
};

type GetMonthsFromRangeType = {
  (range: MONTH_RANGE_TO_VALUE_DICTIONARY_TYPEMAP | number): string[];
};

export const getMonthsFromRange: GetMonthsFromRangeType = (range = "LAST_YEAR") => {
  let rangeValue;
  if (typeof range !== "string") {
    // TODO: Set range for custom ranges based on datepicker
  } else {
    rangeValue = MONTH_RANGE_TO_VALUE_DICTIONARY[range];
  }

  if (!rangeValue) return undefined;

  const periodSet = new Set<string>();

  for (let index = 0; index < rangeValue; index += 1) {
    const dateObj = moment().subtract(index, "months").startOf("month");
    const date = dateObj.format("YYYY-MM-DD");
    periodSet.add(date);
  }

  return Array.from(periodSet).sort(sortDateStringArray);
};

type GetWeeksFromRangeType = {
  (range: WEEK_RANGE_TO_VALUE_DICTIONARY_TYPEMAP | number): string[];
};

export const getWeeksFromRange: GetWeeksFromRangeType = (range = "LAST_YEAR") => {
  let rangeValue;
  if (typeof range !== "string") {
    // TODO: Set range for custom ranges based on datepicker
  } else {
    rangeValue = WEEK_RANGE_TO_VALUE_DICTIONARY[range];
  }

  if (!rangeValue) return undefined;

  const weeksSet: Set<string> = new Set();

  for (let index = 0; index < rangeValue; index += 1) {
    const dateObj = moment().subtract(index, "weeks").startOf("isoWeek");
    const date = dateObj.format("YYYY-MM-DD");
    weeksSet.add(date);
  }

  return Array.from(weeksSet).sort(sortDateStringArray);
};

type GetDaysFromRangeType = {
  (range: DAY_RANGE_TO_VALUE_DICTIONARY_TYPEMAP | number): string[];
};

export const getDaysFromRange: GetDaysFromRangeType = (range = "TODAY") => {
  let rangeValue: number;
  if (typeof range !== "string") {
    // TODO: Set range for custom ranges based on datepicker
  } else {
    rangeValue = DAY_RANGE_TO_VALUE_DICTIONARY[range];
  }

  if (!rangeValue) return undefined;

  const daysSet = new Set<string>();

  for (let index = 0; index < rangeValue; index += 1) {
    const dateObj = moment().subtract(index, "days");
    const date = dateObj.format("YYYY-MM-DD");
    daysSet.add(date);
  }

  return Array.from(daysSet).sort(sortDateStringArray);
};

type DatePeriodType = {
  toDate: moment.Moment;
  fromDate: moment.Moment;
};
type GetDateDifferenceType = {
  (period: DatePeriodType): number;
};

export const getDateDifference: GetDateDifferenceType = ({ toDate, fromDate }) => {
  return toDate.diff(fromDate, "days");
};

const getHistogramCustomInterval = (dateRange: DatePeriodType) => {
  const diff = getDateDifference(dateRange);

  if (diff <= 30) return getDaysFromRange(diff);
  if (diff <= 90) return getWeeksFromRange(diff);
  return getMonthsFromRange(diff);
};

type GetHistogramAxisFromDateRangeType = {
  (dateRange: RANGE_VALUE, mobile?: boolean): string[];
};

export const getHistogramAxisFromDateRange: GetHistogramAxisFromDateRangeType = (
  dateRange,
  mobile = false
) => {
  if (typeof dateRange !== "string") {
    return getHistogramCustomInterval(dateRange);
  }

  const DYNAMIC_DATE_RANGE_HISTOGRAM_INTERVAL = mobile
    ? DATE_RANGE_DEFAULT_HISTOGRAM_INTERVAL_FOR_MOBILE_DEVICES[
        dateRange as MONTH_RANGE_TO_VALUE_DICTIONARY_TYPEMAP
      ]
    : DATE_RANGE_DEFAULT_HISTOGRAM_INTERVAL[dateRange as MONTH_RANGE_TO_VALUE_DICTIONARY_TYPEMAP];

  switch (DYNAMIC_DATE_RANGE_HISTOGRAM_INTERVAL) {
    case "MONTH":
      return getMonthsFromRange(dateRange as MONTH_RANGE_TO_VALUE_DICTIONARY_TYPEMAP);
    case "WEEK":
      return getWeeksFromRange(dateRange as WEEK_RANGE_TO_VALUE_DICTIONARY_TYPEMAP);
    case "DAY":
      return getDaysFromRange(dateRange as DAY_RANGE_TO_VALUE_DICTIONARY_TYPEMAP);
    default:
      throw Error(`DYNAMIC_DATE: ${DYNAMIC_DATE_RANGE_HISTOGRAM_INTERVAL} Not Found.`);
  }
};
